import React, { memo, useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Dialog } from "@shared/components";
import { InvestorRequestType } from "@investorRequest/models";
import GeneralRequest from "@investorRequest/containers/GeneralRequest";
import WireInstructionsRequest from "@investorRequest/containers/WireInstructionsRequest";
import WireInstructionsRequestLegacy from "@investorRequest/containers/WireInstructionsRequestLegacy";
import NoDataRequest from "@investorRequest/containers/NoDataRequest";
import InvestorRequestControls from "@investorRequest/containers/InvestorRequestControls";
import WireInstructionsControls from "@investorRequest/containers/WireInstructionsControls";
import InvestorRequestTypesDropdown from "@investorRequest/components/InvestorRequestTypesDropdown";
import InvestorRequestFormTitle from "@investorRequest/components/InvestorRequestFormTitle";
import InvestorRequestLoadingPlaceholder from "@investorRequest/components/InvestorRequestLoadingPlaceholder";
import {
  closeInvestorRequestDialog,
  resetInvestorRequestState,
  setWireUnavailable,
  selectRequestType,
} from "@investorRequest/store/ducks";
import { investorRequestSelector } from "@investorRequest/store/selectors";
import { submitFormThunk, checkC2DSAvaliabilityThunk } from "@investorRequest/store/thunks";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";

const InvestorRequestDialog = () => {
  const { open, requestType, loading, wireUnavailable } = useAppSelector(investorRequestSelector);
  const dispatch = useAppDispatch();
  const isExpandedWireEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ExpandedWireInstructions);

  const TransitionProps = useMemo<TransitionProps>(
    () => ({
      onExited: () => dispatch(resetInvestorRequestState()),
    }),
    [dispatch]
  );

  const handleCloseDialog = useCallback(() => {
    dispatch(closeInvestorRequestDialog());
  }, [dispatch]);

  const handleSelectRequestType = useCallback(
    async (requestType: InvestorRequestType) => {
      if (isExpandedWireEnabled && requestType === InvestorRequestType.UpdateWireInstructions) {
        const result = await dispatch(checkC2DSAvaliabilityThunk());

        dispatch(setWireUnavailable(!result.payload));
        dispatch(selectRequestType({ requestType }));
      } else {
        dispatch(setWireUnavailable(false));
        dispatch(selectRequestType({ requestType }));
      }
    },
    [dispatch, isExpandedWireEnabled]
  );

  const handleSubmit = useCallback(() => dispatch(submitFormThunk()), [dispatch]);
  const isMobile = useMobileLayout();

  const isExpandedWire = isExpandedWireEnabled && requestType === InvestorRequestType.UpdateWireInstructions;

  const ControlsComponent = isExpandedWire ? WireInstructionsControls : InvestorRequestControls;

  const shouldRenderControlsInActions = !isMobile && !isExpandedWire;

  const WireComponent = isExpandedWireEnabled ? WireInstructionsRequest : WireInstructionsRequestLegacy;

  return (
    <Dialog
      onClose={handleCloseDialog}
      open={open}
      TransitionProps={TransitionProps}
      title="New Request"
      maxWidth="lg"
      fullWidth
      actions={
        shouldRenderControlsInActions ? <ControlsComponent onSubmit={handleSubmit} loading={loading} /> : undefined
      }
    >
      <Grid container spacing={isMobile ? 7.5 : 10}>
        <InvestorRequestFormTitle text="Have a question? Let us know, we are listening." />
        <InvestorRequestTypesDropdown requestType={requestType} onSelect={handleSelectRequestType} />
        {loading && requestType ? (
          <InvestorRequestLoadingPlaceholder />
        ) : (
          <>
            {requestType === InvestorRequestType.General && <GeneralRequest />}
            {requestType === InvestorRequestType.UpdateWireInstructions &&
              (!isExpandedWireEnabled || !wireUnavailable) && <WireComponent />}
            {requestType === InvestorRequestType.NoData && <NoDataRequest />}
          </>
        )}
        {!shouldRenderControlsInActions && <ControlsComponent onSubmit={handleSubmit} loading={loading} />}
      </Grid>
    </Dialog>
  );
};

export default memo(InvestorRequestDialog);
